import { useChatsPaginated } from "#app/hooks/use-chats-paginated";
import { useChatStore } from "#app/store/chat.ts";
import { type ChatThread, useSessionsStore } from "#app/store/sessions";
import { useNavigate } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { deleteChat } from "#app/utils/workflow-svc";
import { getDesignPhaseBlockingState } from "#app/utils/design-phase-guard";
import { toast } from "sonner";

type UseSessionPanelOptions = {
  isExpanded?: boolean;
};

export function useSessionPanel(options?: UseSessionPanelOptions) {
  const { t } = useTranslation("chat");
  const isExpanded = options?.isExpanded ?? true;
  const navigate = useNavigate();
  const {
    isLoading: isLoadingChats,
    error: chatsError,
    hasMore: hasMoreChats,
    loadMore: loadMoreChats,
  } = useChatsPaginated();
  const currentSessionId = useChatStore((s) => s.currentSessionId);
  const resetChat = useChatStore((s) => s.resetChat);
  const plannedWorkflow = useChatStore((s) => s.plannedWorkflow);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const isDesignTimeExecuting = useChatStore((s) => s.isDesignTimeExecuting);
  const isWaitingForInput = useChatStore((s) => s.isWaitingForInput);
  const workflowRunStatus = useChatStore((s) => s.workflowRunStatus);
  const currentThinking = useChatStore((s) => s.currentThinking);
  const lastStreamEvent = useChatStore((s) => s.lastStreamEvent);
  const sessionsChats = useSessionsStore((s) => s.chats);

  const currentSessionWorkflowId = useMemo(() => {
    if (!currentSessionId) return null;
    const currentChat = sessionsChats.find((c) => c.id === currentSessionId);
    return currentChat?.workflowId ?? null;
  }, [currentSessionId, sessionsChats]);

  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "chat";
    id: string;
    name: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSessionsExpanded, setIsSessionsExpanded] = useState(true);
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleNewJob = (onAfterNavigate?: () => void) => {
    resetChat();
    navigate("/u/alpha/agent");
    onAfterNavigate?.();
  };

  const handleSelectJob = (jobId: string, onAfterNavigate?: () => void) => {
    navigate(`/u/alpha/agent/${jobId}`);
    onAfterNavigate?.();
  };

  const localizeChatTitle = useCallback(
    (title: string) => {
      const normalized = title.trim().toLowerCase();
      if (normalized === "new chat" || normalized === "new job") {
        return t("newChat");
      }
      return title;
    },
    [t],
  );

  const chatDataSource = useMemo(() => {
    return sessionsChats.map((chat: ChatThread) => ({
      key: chat.id,
      title: localizeChatTitle(chat.title),
      lastUpdatedAt: chat.lastUpdatedAt,
      workflowId: chat.workflowId,
      isTryMode: chat.isTryMode,
    }));
  }, [sessionsChats, localizeChatTitle]);

  const filteredChatDataSource = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return chatDataSource;
    return chatDataSource.filter((c) => c.title.toLowerCase().includes(q));
  }, [chatDataSource, searchQuery]);

  const handleDeleteChat = (chatId: string, chatTitle: string) => {
    setDeleteTarget({ type: "chat", id: chatId, name: chatTitle });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    setDeletingChatId(id);
    try {
      await deleteChat(id);
      toast.success(
        i18next.t("sessionPanel.jobDeletedSuccess", { name, ns: "chat" }),
      );
      if (id === currentSessionId) {
        navigate("/u/alpha/agent");
      }
      window.location.reload();
    } catch (error) {
      console.error("Failed to delete job:", error);
      toast.error(
        i18next.t("sessionPanel.jobDeleteFailed", { name, ns: "chat" }),
      );
    } finally {
      setDeletingChatId(null);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  useEffect(() => {
    if (isExpanded && shouldFocusSearch && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldFocusSearch(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, shouldFocusSearch]);

  return {
    currentSessionId,
    isLoadingChats,
    chatsError,
    hasMoreChats,
    loadMoreChats,
    filteredChatDataSource,
    searchQuery,
    setSearchQuery,
    searchInputRef,
    isSessionsExpanded,
    setIsSessionsExpanded,
    deletingChatId,
    deleteModalOpen,
    deleteTarget,
    handleDeleteChat,
    confirmDelete,
    cancelDelete,
    handleNewJob,
    handleSelectJob,
    setShouldFocusSearch,
  };
}
